import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { useBulkBatchUploadErrors, useCheckTypes } from '../hooks/useMasterData';
import SearchableDropdown from '../components/SearchableDropdown';
import RowValidationErrorsCell from '../components/RowValidationErrorsCell';
import Tooltip from '../components/Tooltip';
import UserTimestamp from '../components/UserTimestamp';
import UploadProgressCard from '../components/UploadProgressCard';
import { MdOutlineVisibility, MdOutlineEdit } from 'react-icons/md';
import { Trash2, ArrowUpCircle } from 'lucide-react';
import { formatDateUS } from '../utils/dateUtils';
import { resolveUploadJobName } from '../utils/uploadJobName';
import USDateInput from '../components/USDateInput';
import * as XLSX from 'xlsx';
import './CheckUpload.css';
import './CheckDetails.css';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

const formatDateTime = (dt) => {
  if (!dt) return '-';
  return new Date(dt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const formatRowNumber = (n) => {
  if (n == null) return '-';
  const num = Number(n);
  return isNaN(num) ? String(n) : num >= 1000 ? String(num) : String(num).padStart(3, '0');
};

const getRowStatusStyle = (rowStatus) => {
  const map = {
    VALID: { background: '#d1fae5', color: '#059669' },
    INVALID: { background: '#fee2e2', color: '#dc2626' },
    PENDING: { background: '#fef3c7', color: '#d97706' },
    PROMOTED: { background: '#dbeafe', color: '#2563eb' },
  };
  return map[rowStatus] || { background: '#f3f4f6', color: '#6b7280' };
};

const getJobStatusStyle = (status) => {
  const map = {
    READY_TO_PROMOTE: { background: '#d1fae5', color: '#059669' },
    CORRECTION_REQUIRED: { background: '#fee2e2', color: '#dc2626' },
    COMPLETED: { background: '#d1fae5', color: '#059669' },
    FAILED: { background: '#fee2e2', color: '#dc2626' },
    PENDING: { background: '#fef3c7', color: '#d97706' },
    PROMOTED: { background: '#dbeafe', color: '#2563eb' },
  };
  return map[status] || { background: '#f3f4f6', color: '#6b7280' };
};

const formatJobStatus = (status) => {
  if (!status) return 'PENDING';
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

const BATCH_SOURCE_OPTIONS = ['Athena', 'Centricity'];

const normalizeBatchType = (t) => {
  const u = String(t || '').trim().toUpperCase();
  return u === 'AUTO' ? 'AUTO' : 'MANUAL';
};

/** Run # is editable for AUTO only; locked for MANUAL */
const isRunNumberDisabledForBatchType = (t) => normalizeBatchType(t) === 'MANUAL';
/** EDM # is editable for MANUAL only; locked for AUTO */
const isEdmNumberDisabledForBatchType = (t) => normalizeBatchType(t) === 'AUTO';

const normalizeBatchSource = (s) => {
  const v = String(s ?? '').trim();
  return BATCH_SOURCE_OPTIONS.includes(v) ? v : 'Athena';
};

/** Parse API/string values into boolean for correction batch checkbox */
const parseCorrectionBatch = (v) => {
  if (typeof v === 'boolean') return v;
  if (v == null || v === '') return false;
  const s = String(v).trim().toLowerCase();
  if (s === 'true' || s === 'yes' || s === '1' || s === 'y') return true;
  if (s === 'false' || s === 'no' || s === '0' || s === 'n') return false;
  return Boolean(v);
};

const applyBatchFieldRules = (prev, field, value) => {
  const newData = { ...prev, [field]: value };
  // AUTO: Run Number (EDM disabled). MANUAL: EDM Number (Run disabled) — clear the inactive field on type change.
  if (field === 'batchType' && value === 'MANUAL') {
    newData.batchRunNumber = '';
  }
  if (field === 'batchType' && value === 'AUTO') {
    newData.edmNumber = '';
  }
  if (field === 'correctionBatch') {
    newData.correctionBatch = value;
  }
  return newData;
};

const normalizeCheckTypeCode = (raw, types) => {
  if (raw == null || raw === '') return '';
  const r = String(raw).trim();
  const found = (types || []).find((ct) => String(ct.code).toLowerCase() === r.toLowerCase());
  return found ? found.code : r;
};

const formatCheckTypeForDisplay = (raw, types) => {
  if (raw == null || raw === '') return '-';
  const r = String(raw).trim();
  const found = (types || []).find((ct) => String(ct.code).toLowerCase() === r.toLowerCase());
  return found ? found.name : r;
};

const applyBatchTypeToPayload = (batchType, payload) => {
  const t = normalizeBatchType(batchType);
  const next = { ...payload };
  if (t === 'AUTO') {
    next.edmNumber = null;
  } else {
    next.batchRunNumber = null;
  }
  return next;
};

const TruncatedText = ({ text, maxLength = 25 }) => {
  if (!text) return <span>-</span>;
  const should = text.length > maxLength;
  const display = should ? text.substring(0, maxLength) + '...' : text;
  return should ? (
    <Tooltip text={text} position="bottom">
      <span className="truncated-text">{display}</span>
    </Tooltip>
  ) : <span>{display}</span>;
};

const TruncatedName = ({ text, maxLength = 12 }) => {
  if (!text) return <span>-</span>;
  const should = text.length > maxLength;
  const display = should ? text.substring(0, maxLength) + '...' : text;
  return should ? (
    <Tooltip text={text} position="bottom">
      <span className="truncated-text">{display}</span>
    </Tooltip>
  ) : <span>{display}</span>;
};

const ErrorsBlock = ({ validationErrors, validationErrorInfo }) => {
  const hasErrors = validationErrors && validationErrors.length > 0;
  const hasInfo = !!validationErrorInfo;
  if (!hasErrors && !hasInfo) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {hasErrors && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '8px 12px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#dc2626', textTransform: 'uppercase', marginBottom: '6px' }}>Validation Errors</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px' }}>
            {validationErrors.map((e, i) => (
              <div key={i} style={{ fontSize: '13px', color: '#991b1b', display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
                <span style={{ color: '#dc2626', marginTop: '1px', flexShrink: 0 }}>•</span>
                <span>{e}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {hasInfo && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '8px 12px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#d97706', textTransform: 'uppercase', marginBottom: '4px' }}>Info</div>
          <div style={{ fontSize: '13px', color: '#92400e' }}>{validationErrorInfo}</div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, wide }) => (
  <div style={{ gridColumn: wide ? '1 / -1' : 'auto', marginBottom: '12px' }}>
    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>{label}</div>
    <div style={{ fontSize: '14px', color: '#111827', wordBreak: 'break-word' }}>{value || <span style={{ color: '#9ca3af' }}>—</span>}</div>
  </div>
);

const EMPTY_EDIT = {
  checkNumber: '',
  practiceCode: '',
  locationCode: '',
  checkType: '',
  depositDate: '',
  checkAmount: '',
  batchType: 'MANUAL',
  batchRunNumber: '',
  batchNumber: '',
  invoiceNumber: '',
  edmNumber: '',
  batchDate: '',
  batchAmount: '',
  correctionBatch: false,
  batchNotes: '',
  batchSource: 'Athena',
};

const BatchUpload = () => {
  const { showError, showSuccess } = useNotification();
  const { getUserById, getUserName } = useUsers();
  const { data: batchErrorTypes, loading: batchErrorTypesLoading } = useBulkBatchUploadErrors(true);
  const { data: checkTypes, loading: checkTypesLoading } = useCheckTypes(true);

  const [dashboardJobs, setDashboardJobs] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobSearch, setJobSearch] = useState('');
  const [dashboardPage, setDashboardPage] = useState(0);
  const [dashboardTotalElements, setDashboardTotalElements] = useState(0);
  const [dashboardTotalPages, setDashboardTotalPages] = useState(0);
  const [dashboardHasMore, setDashboardHasMore] = useState(false);
  const [dashboardPageInputValue, setDashboardPageInputValue] = useState('1');

  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jobName, setJobName] = useState('');
  const [practiceCode, setPracticeCode] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState({
    active: false,
    percent: 0,
    stageIndex: 0,
    stageLabel: 'Preparing',
    helperText: '',
    indeterminate: false,
  });

  const [practices, setPractices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedPracticeId, setSelectedPracticeId] = useState('');

  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [filterErrors, setFilterErrors] = useState([]);
  const [rowSearch, setRowSearch] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  const [viewingRow, setViewingRow] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState(EMPTY_EDIT);
  const [savingEdit, setSavingEdit] = useState(false);

  const [revalidating, setRevalidating] = useState(false);
  const [promotingAll, setPromotingAll] = useState(false);
  const [promotingRowId, setPromotingRowId] = useState(null);
  const [promoteModalRow, setPromoteModalRow] = useState(null);
  const [promoteFormData, setPromoteFormData] = useState(null);
  const [promotingFromModal, setPromotingFromModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  /** Practice/location dropdowns inside row Edit / Promote modals */
  const [rowFormPracticeId, setRowFormPracticeId] = useState('');
  const [rowFormLocations, setRowFormLocations] = useState([]);
  const [loadingRowFormLocations, setLoadingRowFormLocations] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoadingPractices(true);
      try {
        const data = await api.getAllPractices();
        setPractices((Array.isArray(data) ? data : []).filter(p => p.isActive !== false));
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingPractices(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    if (!selectedPracticeId) { setLocations([]); setLocationCode(''); return; }
    const fetch = async () => {
      setLoadingLocations(true);
      try {
        const data = await api.getPracticeLocations(selectedPracticeId);
        setLocations((Array.isArray(data) ? data : []).filter(l => l.isActive !== false));
      } catch (e) {
        console.error(e);
        setLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetch();
  }, [selectedPracticeId]);

  useEffect(() => {
    if (!rowFormPracticeId) {
      setRowFormLocations([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingRowFormLocations(true);
      try {
        const data = await api.getPracticeLocations(rowFormPracticeId);
        if (!cancelled) {
          setRowFormLocations((Array.isArray(data) ? data : []).filter((l) => l.isActive !== false));
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setRowFormLocations([]);
      } finally {
        if (!cancelled) setLoadingRowFormLocations(false);
      }
    })();
    return () => { cancelled = true; };
  }, [rowFormPracticeId]);

  const fetchDashboard = useCallback(async (page = 0, searchText = '') => {
    setLoadingDashboard(true);
    try {
      const search = String(searchText || '').trim();
      const response = await api.getBulkBatchUploadJobs({
        page,
        size: 50,
        ...(search ? { jobName: search } : {}),
      });
      setDashboardJobs(response?.items || []);
      const totalPagesCount = response?.totalPages || 0;
      setDashboardTotalElements(response?.totalElements || 0);
      setDashboardTotalPages(totalPagesCount);
      setDashboardHasMore(page < totalPagesCount - 1);
      setDashboardPage(page);
      setDashboardPageInputValue((page + 1).toString());
    } catch (err) {
      const msg = err.data?.message || err.message || 'Failed to load jobs.';
      showError(msg);
    } finally {
      setLoadingDashboard(false);
    }
  }, [showError]);

  const fetchJobRows = useCallback(async (jobId, page = 0, errorSearches = []) => {
    setLoadingRows(true);
    try {
      const [jobDetails, response] = await Promise.all([
        api.getBulkBatchUploadJob(jobId),
        api.getBulkBatchUploadJobRows(jobId, { page, size: 50, errorSearches }),
      ]);
      setSelectedJob(jobDetails);
      setRows(response.items || []);
      setTotalElements(response.totalElements || 0);
      setTotalPages(response.totalPages || 0);
      setCurrentPage(page);
    } catch (err) {
      const msg = err.data?.message || err.message || 'Failed to load rows.';
      showError(msg);
    } finally {
      setLoadingRows(false);
    }
  }, [showError]);

  useEffect(() => { fetchDashboard(dashboardPage, jobSearch); }, [fetchDashboard, dashboardPage, jobSearch]);

  useEffect(() => {
    setDashboardPage(0);
    setDashboardPageInputValue('1');
  }, [jobSearch]);

  const isValidFileType = (f) =>
    ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
      .includes(f.type) || f.name.endsWith('.csv') || f.name.endsWith('.xlsx');

  const processSelectedFile = async (f) => {
    if (!isValidFileType(f)) { showError('Please select a CSV or XLSX file'); setFile(null); return; }
    if (f.size > 10 * 1024 * 1024) { showError('Maximum allowed file size is 10 MB.'); setFile(null); return; }
    setFile(f);
  };

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) await processSelectedFile(dropped);
  };
  const handleFileInput = async (e) => {
    const selected = e.target.files?.[0];
    if (selected) await processSelectedFile(selected);
    e.target.value = '';
  };

  const resetUploadModal = () => {
    setFile(null); setJobName(''); setPracticeCode(''); setLocationCode(''); setSelectedPracticeId('');
    setUploadProgress((prev) => ({ ...prev, active: false }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!practiceCode) { showError('Practice is required'); return; }
    if (!locationCode) { showError('Location is required'); return; }
    setUploading(true);
    setUploadProgress({
      active: true,
      percent: 10,
      stageIndex: 0,
      stageLabel: 'Preparing file',
      helperText: 'Preparing request and metadata...',
      indeterminate: false,
    });
    try {
      setUploadProgress({
        active: true,
        percent: 30,
        stageIndex: 1,
        stageLabel: 'Validating input',
        helperText: 'Checking selected practice and location...',
        indeterminate: false,
      });
      const resolvedJobName = resolveUploadJobName(jobName, { practiceCode, locationCode });
      const response = await api.createBulkBatchUploadJob(file, practiceCode, locationCode, resolvedJobName, {
        onUploadProgress: (progressEvent) => {
          const uploadPercent = progressEvent?.progress;
          const mapped = uploadPercent == null
            ? 62
            : Math.min(80, Math.max(45, 45 + Math.round((uploadPercent / 100) * 35)));
          setUploadProgress({
            active: true,
            percent: mapped,
            stageIndex: 2,
            stageLabel: 'Uploading file',
            helperText: uploadPercent == null
              ? 'Uploading staged rows...'
              : `Uploading... ${uploadPercent}% complete`,
            indeterminate: uploadPercent == null,
          });
        },
      });
      setUploadProgress({
        active: true,
        percent: 92,
        stageIndex: 3,
        stageLabel: 'Finalizing validation',
        helperText: 'Completing server validation and creating job...',
        indeterminate: true,
      });
      showSuccess('File uploaded successfully!');
      setShowUploadModal(false); resetUploadModal();
      await fetchDashboard(dashboardPage, jobSearch);
      if (response.job) await fetchJobRows(response.job.jobId, 0);
    } catch (err) {
      showError(err.data?.message || err.message || 'Failed to upload file.');
      setUploadProgress({
        active: true,
        percent: 100,
        stageIndex: 3,
        stageLabel: 'Upload failed',
        helperText: err.data?.message || err.message || 'Failed to upload file.',
        indeterminate: false,
      });
    } finally { setUploading(false); }
  };

  const handleSelectJob = async (job) => {
    setSelectedJob(job);
    setFilterErrors([]);
    await fetchJobRows(job.jobId, 0, []);
  };

  const handleFilterChange = (val) => {
    const newErrors = val ? [val] : [];
    setFilterErrors(newErrors);
    if (selectedJob) fetchJobRows(selectedJob.jobId, 0, newErrors);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: '#6366f1' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleEditBatchFormChange = useCallback((field, value) => {
    setEditFormData((prev) => applyBatchFieldRules(prev, field, value));
  }, []);

  const handlePromoteBatchFormChange = useCallback((field, value) => {
    setPromoteFormData((prev) => (prev ? applyBatchFieldRules(prev, field, value) : prev));
  }, []);

  const resolvePracticeIdFromCode = useCallback((code) => {
    if (!code) return '';
    const c = String(code).trim().toLowerCase();
    const p = practices.find((x) => String(x.code ?? '').trim().toLowerCase() === c);
    return p ? (p.practiceId || p.id) : '';
  }, [practices]);

  const handleEditRow = (row) => {
    setEditingRow(row);
    setRowFormPracticeId(resolvePracticeIdFromCode(row.practiceCode));
    setEditFormData({
      checkNumber: row.checkNumber || '',
      practiceCode: row.practiceCode || '',
      locationCode: row.locationCode || '',
      checkType: normalizeCheckTypeCode(row.checkType, checkTypes),
      depositDate: row.depositDate || '',
      checkAmount: row.checkAmount ?? '',
      batchType: normalizeBatchType(row.batchType),
      batchRunNumber: row.batchRunNumber || '',
      batchNumber: row.batchNumber || '',
      invoiceNumber: row.invoiceNumber || '',
      edmNumber: row.edmNumber || '',
      batchDate: row.batchDate || '',
      batchAmount: row.batchAmount ?? '',
      correctionBatch: parseCorrectionBatch(row.correctionBatch ?? row.isCorrectionBatch),
      batchNotes: row.batchNotes || '',
      batchSource: normalizeBatchSource(row.batchSource),
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRow || !selectedJob) return;
    setSavingEdit(true);
    try {
      const payload = applyBatchTypeToPayload(editFormData.batchType, {
        ...editFormData,
        checkAmount: editFormData.checkAmount !== '' ? Number(editFormData.checkAmount) : null,
        batchAmount: editFormData.batchAmount !== '' ? Number(editFormData.batchAmount) : null,
        correctionBatch: !!editFormData.correctionBatch,
      });
      await api.updateBulkBatchUploadRow(selectedJob.jobId, editingRow.stagingBatchId, payload);
      const updatedJob = await api.revalidateBulkBatchUploadJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      setEditingRow(null);
      setEditFormData(EMPTY_EDIT);
      setRowFormPracticeId('');
      showSuccess('Row updated and re-validated!');
      await fetchJobRows(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to update row.');
    } finally {
      setSavingEdit(false);
    }
  };

  const openPromoteModal = (row) => {
    setPromoteModalRow(row);
    setRowFormPracticeId(resolvePracticeIdFromCode(row.practiceCode));
    setPromoteFormData({
      checkNumber: row.checkNumber || '',
      practiceCode: row.practiceCode || '',
      locationCode: row.locationCode || '',
      checkType: normalizeCheckTypeCode(row.checkType, checkTypes),
      depositDate: row.depositDate || '',
      checkAmount: row.checkAmount ?? '',
      batchType: normalizeBatchType(row.batchType),
      batchRunNumber: row.batchRunNumber || '',
      batchNumber: row.batchNumber || '',
      invoiceNumber: row.invoiceNumber || '',
      edmNumber: row.edmNumber || '',
      batchDate: row.batchDate || '',
      batchAmount: row.batchAmount ?? '',
      correctionBatch: parseCorrectionBatch(row.correctionBatch ?? row.isCorrectionBatch),
      batchNotes: row.batchNotes || '',
      batchSource: normalizeBatchSource(row.batchSource),
    });
  };

  const closePromoteModal = () => {
    if (promotingFromModal) return;
    setPromoteModalRow(null);
    setPromoteFormData(null);
    setRowFormPracticeId('');
  };

  const handleConfirmPromoteFromModal = async () => {
    if (!selectedJob || !promoteModalRow || !promoteFormData) return;
    setPromotingFromModal(true);
    setPromotingRowId(promoteModalRow.stagingBatchId);
    try {
      const payload = applyBatchTypeToPayload(promoteFormData.batchType, {
        ...promoteFormData,
        checkAmount: promoteFormData.checkAmount !== '' ? Number(promoteFormData.checkAmount) : null,
        batchAmount: promoteFormData.batchAmount !== '' ? Number(promoteFormData.batchAmount) : null,
        correctionBatch: !!promoteFormData.correctionBatch,
      });
      await api.updateBulkBatchUploadRow(selectedJob.jobId, promoteModalRow.stagingBatchId, payload);
      const updatedJob = await api.revalidateBulkBatchUploadJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      await api.promoteBulkBatchUploadRow(selectedJob.jobId, promoteModalRow.stagingBatchId);
      showSuccess('Row promoted successfully!');
      setPromoteModalRow(null);
      setPromoteFormData(null);
      await fetchJobRows(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      const msg = err?.data?.message || err?.message || String(err);
      showError(msg);
    } finally {
      setPromotingFromModal(false);
      setPromotingRowId(null);
    }
  };

  const handlePromoteAll = async () => {
    if (!selectedJob) return;
    setPromotingAll(true);
    try {
      const updatedJob = await api.promoteBulkBatchUploadJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      showSuccess('Valid rows promoted successfully!');
      await fetchDashboard(dashboardPage, jobSearch);
      await fetchJobRows(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to promote rows.';
      showError(msg);
    } finally {
      setPromotingAll(false);
    }
  };

  const handleRevalidate = async () => {
    if (!selectedJob) return;
    setRevalidating(true);
    try {
      const updatedJob = await api.revalidateBulkBatchUploadJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      showSuccess('Job re-validated successfully!');
      await fetchJobRows(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to re-validate job.';
      showError(msg);
    } finally {
      setRevalidating(false);
    }
  };

  const handleExportReport = async () => {
    if (!selectedJob) return;
    setExporting(true);
    try {
      const blob = await api.exportBulkBatchUploadReport(selectedJob.jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedJob.jobName || 'BatchUpload'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Report exported successfully!');
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to export report.';
      showError(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteClick = (job, e) => {
    e.stopPropagation();
    setJobToDelete(job);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete) return;
    setDeleting(true);
    try {
      await api.deleteBulkBatchUploadJob(jobToDelete.jobId);
      showSuccess(`Job "${jobToDelete.jobName || 'Unnamed Job'}" deleted successfully!`);
      if (selectedJob?.jobId === jobToDelete.jobId) { setSelectedJob(null); setRows([]); }
      await fetchDashboard(dashboardPage, jobSearch);
      setShowDeleteConfirm(false); setJobToDelete(null);
    } catch (err) {
      showError(err.data?.message || err.message || 'Failed to delete job.');
    } finally {
      setDeleting(false);
    }
  };

  const displayedRows = (() => {
    let list = rows;
    if (rowSearch.trim()) {
      const q = rowSearch.trim().toLowerCase();
      list = rows.filter(r => {
        const text = [
          r.sheetRowNumber, r.rowStatus, r.checkNumber, r.practiceCode, r.locationCode, r.checkType,
          r.depositDate, r.batchType, r.batchRunNumber, r.batchNumber, r.invoiceNumber, r.edmNumber,
          r.batchDate, r.batchSource, r.batchNotes, ...(r.validationErrors || []), r.validationErrorInfo,
        ].filter(Boolean).join(' ').toLowerCase();
        return text.includes(q);
      });
    }
    if (!sortField) return list;
    return [...list].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (['depositDate', 'batchDate'].includes(sortField)) {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (['checkAmount', 'batchAmount', 'sheetRowNumber'].includes(sortField)) {
        aVal = Number(aVal ?? 0);
        bVal = Number(bVal ?? 0);
      } else {
        aVal = (aVal ?? '').toString().toLowerCase();
        bVal = (bVal ?? '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const practiceOptions = [
    { value: '', label: 'Select Practice' },
    ...practices.map(p => ({ value: p.practiceId || p.id, label: `${p.name} (${p.code})`, code: p.code })),
  ];
  const locationOptions = [
    { value: '', label: 'Select Location' },
    ...locations.map(l => ({ value: l.code, label: `${l.name} (${l.code})` })),
  ];
  const rowFormLocationOptions = [
    { value: '', label: 'Select Location' },
    ...rowFormLocations.map((l) => ({ value: l.code, label: `${l.name} (${l.code})` })),
  ];
  const errorFilterOptions = [
    { value: '', label: 'All Errors' },
    ...(batchErrorTypes || []).map(e => ({ value: e.code, label: e.name })),
  ];

  const isProcessing = uploading || revalidating || deleting || exporting || promotingAll;
  const handleDashboardPreviousPage = () => {
    if (dashboardPage > 0 && !loadingDashboard) {
      setDashboardPage((prev) => prev - 1);
    }
  };

  const handleDashboardNextPage = () => {
    if (dashboardHasMore && !loadingDashboard) {
      setDashboardPage((prev) => prev + 1);
    }
  };

  const handleDashboardPageInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setDashboardPageInputValue(value);
    }
  };

  const handleDashboardGoToPage = () => {
    if (dashboardPageInputValue === '' || loadingDashboard) return;
    const pageNum = parseInt(dashboardPageInputValue, 10);
    const maxPage = dashboardTotalPages > 0 ? dashboardTotalPages - 1 : 0;
    if (pageNum >= 1 && pageNum <= maxPage + 1) {
      setDashboardPage(pageNum - 1);
    } else {
      setDashboardPageInputValue((dashboardPage + 1).toString());
    }
  };

  const handleDashboardPageInputKeyPress = (e) => {
    if (e.key === 'Enter') handleDashboardGoToPage();
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'Check Number': '',
        'Practice Code': '',
        'Location Code': '',
        'Check Type': '',
        'Deposit Date': '',
        'Check Amount': '',
        'Batch Type': '',
        'Batch Source': '',
        'Batch Run Number': '',
        'Batch Number': '',
        'Invoice Number': '',
        'EDM Number': '',
        'Batch Date': '',
        'Batch Amount': '',
        'Correction Batch': '',
        'Batch Notes': '',
      },
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [
      { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
      { wch: 28 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Batch Upload Template');
    XLSX.writeFile(wb, 'BatchUpload_Template.xlsx');
  };

  return (
    <div className="check-upload-page">
      {!selectedJob && (
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>Batch Upload</h1>
            <p className="page-subtitle" style={{ margin: 0 }}>Bulk upload staged batch rows and validate before promotion</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className="btn-cancel"
              onClick={handleDownloadTemplate}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M3 17C3 18.1046 3.89543 19 5 19H15C16.1046 19 17 18.1046 17 17V13M13 10L10 13M10 13L7 10M10 13V1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download Template
            </button>
            <button className="btn-upload-small" onClick={() => setShowUploadModal(true)} disabled={isProcessing}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginRight: '6px' }}>
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 13H8M12 9V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Batch Upload
            </button>
            <Tooltip text="Download a sample XLSX column layout, then upload your batch file for staging and validation" position="bottom">
              <div className="info-icon-wrapper">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M10 14V10M10 6H10.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </Tooltip>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="modal-overlay" onClick={() => { if (!uploading) { setShowUploadModal(false); resetUploadModal(); } }}>
          <div className="upload-modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>Upload Batch File</h2>
              <button onClick={() => { if (!uploading) { setShowUploadModal(false); resetUploadModal(); } }} disabled={uploading}
                style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: uploading ? 'not-allowed' : 'pointer', padding: '0', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>×</button>
            </div>
            <div className="upload-section-modal">
              <div className={`upload-zone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
                <input ref={fileInputRef} type="file" id="bb-file-input" accept=".csv,.xlsx" onChange={handleFileInput} style={{ display: 'none' }} />
                {!file ? (
                  <>
                    <div className="upload-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 13H8M12 9V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="upload-text"><strong>Click to select CSV or XLSX file</strong><span>or drag and drop here</span></div>
                    <label htmlFor="bb-file-input" className="upload-label">Select File</label>
                  </>
                ) : (
                  <div className="file-selected">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                      <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div className="file-info"><strong>{file.name}</strong><span>{(file.size / 1024).toFixed(2)} KB</span></div>
                    <button className="btn-remove" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} disabled={isProcessing}>Remove</button>
                  </div>
                )}
              </div>
              {file && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Job Name (Optional)</label>
                    <input type="text" value={jobName} onChange={e => setJobName(e.target.value)} placeholder="Enter job name"
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Practice <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <SearchableDropdown
                      options={loadingPractices ? [{ value: '', label: 'Loading...' }] : practiceOptions}
                      value={selectedPracticeId}
                      onChange={(val) => {
                        setSelectedPracticeId(val);
                        const found = practices.find(p => (p.practiceId || p.id) === val);
                        setPracticeCode(found?.code || ''); setLocationCode('');
                      }}
                      placeholder="Select Practice"
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Location <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <SearchableDropdown
                      options={loadingLocations ? [{ value: '', label: 'Loading...' }] : locationOptions}
                      value={locationCode}
                      onChange={setLocationCode}
                      placeholder={!selectedPracticeId ? 'Select practice first' : 'Select Location'}
                      disabled={!selectedPracticeId || loadingLocations}
                    />
                  </div>
                  <div className="upload-actions">
                    <button className="btn-cancel" onClick={resetUploadModal} disabled={isProcessing || uploading}>Cancel</button>
                    <button className="btn-upload" onClick={handleUpload} disabled={uploading || isProcessing || !file || !practiceCode || !locationCode}>
                      {uploading ? 'Uploading...' : 'Upload & Validate'}
                    </button>
                  </div>
                  {uploadProgress.active && (
                    <UploadProgressCard
                      title="Batch upload processing"
                      stageIndex={uploadProgress.stageIndex}
                      stageLabel={uploadProgress.stageLabel}
                      helperText={uploadProgress.helperText}
                      percent={uploadProgress.percent}
                      indeterminate={uploadProgress.indeterminate}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!selectedJob && (
        <>
          <div className="filters-section jobs-search-section">
            <div className="search-bar">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
                <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
                placeholder="Search by job name, file name, practice, or location..."
              />
            </div>
          </div>
          <div className="jobs-section" style={{ marginTop: '0' }}>
            <h2 className="section-title">Batch Upload Jobs</h2>
            {loadingDashboard ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading jobs...</div>
            ) : dashboardJobs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No jobs found</div>
            ) : (
              <div className="jobs-table-wrapper">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <button className="btn-cancel" disabled={dashboardPage === 0 || loadingDashboard} onClick={handleDashboardPreviousPage} title="Previous page" aria-label="Previous page" style={{ padding: '6px 10px', minWidth: '34px' }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    Showing {dashboardTotalElements === 0 ? 0 : dashboardPage * 50 + 1} - {Math.min((dashboardPage + 1) * 50, dashboardTotalElements)} of {dashboardTotalElements}
                  </div>
                  {dashboardTotalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Page:</span>
                      <input
                        type="text"
                        value={dashboardPageInputValue}
                        onChange={handleDashboardPageInputChange}
                        onKeyPress={handleDashboardPageInputKeyPress}
                        onBlur={handleDashboardGoToPage}
                        style={{ width: '46px', height: '26px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                      />
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>of {dashboardTotalPages}</span>
                    </div>
                  )}
                  <button className="btn-cancel" disabled={!dashboardHasMore || loadingDashboard} onClick={handleDashboardNextPage} title="Next page" aria-label="Next page" style={{ padding: '6px 10px', minWidth: '34px' }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Job Name', 'File Name', 'Practice', 'Location', 'Status', 'Total', 'Valid', 'Invalid', 'Promoted', 'Created', 'Updated', 'Actions'].map(h => <th key={h} style={{ padding: '12px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {dashboardJobs.map(job => (
                      <tr key={job.jobId} style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }} onClick={() => handleSelectJob(job)}>
                        <td style={{ padding: '12px' }}><TruncatedName text={job.jobName || 'Unnamed Job'} /></td>
                        <td style={{ padding: '12px' }}><TruncatedName text={job.uploadedFileName || '-'} /></td>
                        <td style={{ padding: '12px' }}>{job.practiceCode || '-'}</td>
                        <td style={{ padding: '12px' }}>{job.locationCode || '-'}</td>
                        <td style={{ padding: '12px' }}>
                          <span className="job-status-badge" style={getJobStatusStyle(job.status)}>{formatJobStatus(job.status)}</span>
                        </td>
                        <td style={{ padding: '12px' }}>{job.totalRows || 0}</td>
                        <td style={{ padding: '12px', color: '#059669' }}>{job.validRows || 0}</td>
                        <td style={{ padding: '12px', color: '#dc2626' }}>{job.invalidRows || 0}</td>
                        <td style={{ padding: '12px', color: '#2563eb' }}>{job.promotedRows || 0}</td>
                        <td style={{ padding: '12px' }}><UserTimestamp userId={job.createdBy} dateTime={job.createdAt} action="created" getUserName={getUserName} getUserById={getUserById} /></td>
                        <td style={{ padding: '12px' }}><UserTimestamp userId={job.updatedBy} dateTime={job.updatedAt} action="updated" getUserName={getUserName} getUserById={getUserById} /></td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                              style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); handleSelectJob(job); }} disabled={isProcessing} title="View">
                              <MdOutlineVisibility size={16} />
                            </button>
                            <button
                              style={{ padding: '6px 8px', color: '#dc2626', border: '1px solid #dc2626', background: 'white', display: 'flex', alignItems: 'center', borderRadius: '6px', cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                              onClick={(e) => handleDeleteClick(job, e)} disabled={isProcessing || deleting} title="Delete job">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {selectedJob && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
              <button
                className="btn-cancel"
                onClick={() => { setSelectedJob(null); setRows([]); setFilterErrors([]); setRowSearch(''); }}
                disabled={isProcessing}
                style={{ flexShrink: 0 }}
              >
                ← Back
              </button>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: '0', fontSize: '20px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedJob.jobName || 'Unnamed Job'}
                </h2>
                <p style={{ margin: '2px 0 0 0', color: '#6b7280', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedJob.uploadedFileName} &bull; Practice: <strong>{selectedJob.practiceCode}</strong> &bull; Location: <strong>{selectedJob.locationCode}</strong> &bull; Created {formatDateTime(selectedJob.createdAt)}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                className="btn-cancel"
                onClick={handleExportReport}
                disabled={exporting || isProcessing || revalidating}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px' }}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M3 17C3 18.1046 3.89543 19 5 19H15C16.1046 19 17 18.1046 17 17V13M13 10L10 13M10 13L7 10M10 13V1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {exporting ? 'Exporting...' : 'Export Report'}
              </button>
              <button className="btn-primary" onClick={handleRevalidate} disabled={revalidating || isProcessing}>
                {revalidating ? 'Re-validating...' : 'Re-validate'}
              </button>
              <button className="btn-upload" onClick={handlePromoteAll} disabled={isProcessing || (selectedJob.validRows || 0) === 0}>
                {promotingAll ? 'Promoting...' : `Promote All (${selectedJob.validRows || 0})`}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Total</div><div style={{ fontSize: '24px' }}>{selectedJob.totalRows || 0}</div></div>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Valid</div><div style={{ fontSize: '24px', color: '#059669' }}>{selectedJob.validRows || 0}</div></div>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Invalid</div><div style={{ fontSize: '24px', color: '#dc2626' }}>{selectedJob.invalidRows || 0}</div></div>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Promoted</div><div style={{ fontSize: '24px', color: '#2563eb' }}>{selectedJob.promotedRows || 0}</div></div>
          </div>

          <div className="filters-section bulk-job-detail-filters">
            <div className="search-bar">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
                <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={rowSearch}
                onChange={(e) => setRowSearch(e.target.value)}
                placeholder="Search rows..."
              />
            </div>
            <div className="filter-dropdowns filter-dropdowns--error-only">
              <SearchableDropdown
                options={batchErrorTypesLoading ? [{ value: '', label: 'Loading...' }] : errorFilterOptions}
                value={filterErrors[0] || ''}
                onChange={handleFilterChange}
                placeholder="All Errors"
              />
              {filterErrors.length > 0 && (
                <button
                  type="button"
                  className="btn-cancel batch-validation-clear-errors"
                  onClick={() => { setFilterErrors([]); if (selectedJob) fetchJobRows(selectedJob.jobId, 0, []); }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>Showing {currentPage * 50 + 1} - {Math.min((currentPage + 1) * 50, totalElements)} of {totalElements}</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn-cancel" disabled={currentPage === 0 || loadingRows} onClick={() => fetchJobRows(selectedJob.jobId, currentPage - 1, filterErrors)}>Previous</button>
              <button className="btn-cancel" disabled={currentPage >= totalPages - 1 || loadingRows} onClick={() => fetchJobRows(selectedJob.jobId, currentPage + 1, filterErrors)}>Next</button>
            </div>
          </div>

          <div className="checks-table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="checks-table" style={{ minWidth: '1400px' }}>
              <thead>
                <tr>
                  {[
                    { label: 'Row', field: 'sheetRowNumber' },
                    { label: 'Status', field: 'rowStatus' },
                    { label: 'Check', field: 'checkNumber' },
                    { label: 'Practice', field: 'practiceCode' },
                    { label: 'Location', field: 'locationCode' },
                    { label: 'Check Type', field: 'checkType' },
                    { label: 'Deposit Date', field: 'depositDate' },
                    { label: 'Check Amount', field: 'checkAmount' },
                    { label: 'Batch', field: 'batchNumber' },
                    { label: 'Run', field: 'batchRunNumber' },
                    { label: 'Batch Date', field: 'batchDate' },
                    { label: 'Batch Amount', field: 'batchAmount' },
                    { label: 'Errors', field: null },
                    { label: 'Actions', field: null },
                  ].map(({ label, field }) => (
                    <th key={label}
                      onClick={field ? () => handleSort(field) : undefined}
                      style={{ padding: '12px', textAlign: 'left', cursor: field ? 'pointer' : 'default', userSelect: 'none' }}>
                      {label}{field && <SortArrow field={field} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadingRows ? (
                  <tr><td colSpan={14} style={{ textAlign: 'center', padding: '40px' }}>Loading rows...</td></tr>
                ) : displayedRows.length === 0 ? (
                  <tr><td colSpan={14} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No rows found</td></tr>
                ) : displayedRows.map(row => {
                  const isPromotingThisRow = promotingRowId === row.stagingBatchId;
                  return (
                    <tr key={row.stagingBatchId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px' }}>{formatRowNumber(row.sheetRowNumber)}</td>
                      <td style={{ padding: '12px' }}>
                        <span className="job-status-badge" style={getRowStatusStyle(row.rowStatus)}>{row.rowStatus || 'UNKNOWN'}</span>
                      </td>
                      <td style={{ padding: '12px' }}><TruncatedText text={row.checkNumber} maxLength={18} /></td>
                      <td style={{ padding: '12px' }}>{row.practiceCode || '-'}</td>
                      <td style={{ padding: '12px' }}>{row.locationCode || '-'}</td>
                      <td style={{ padding: '12px' }}>{formatCheckTypeForDisplay(row.checkType, checkTypes)}</td>
                      <td style={{ padding: '12px' }}>{row.depositDate ? formatDateUS(row.depositDate) : '-'}</td>
                      <td style={{ padding: '12px' }}>{row.checkAmount != null ? formatCurrency(row.checkAmount) : '-'}</td>
                      <td style={{ padding: '12px' }}>{row.batchNumber || '-'}</td>
                      <td style={{ padding: '12px' }}>{row.batchRunNumber || '-'}</td>
                      <td style={{ padding: '12px' }}>{row.batchDate ? formatDateUS(row.batchDate) : '-'}</td>
                      <td style={{ padding: '12px' }}>{row.batchAmount != null ? formatCurrency(row.batchAmount) : '-'}</td>
                      <td style={{ padding: '12px', maxWidth: '220px' }}>
                        <RowValidationErrorsCell
                          validationErrors={row.validationErrors}
                          validationErrorInfo={row.validationErrorInfo}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            title="View details"
                            style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', cursor: 'pointer' }}
                            onClick={() => setViewingRow(row)}
                          >
                            <MdOutlineVisibility size={15} />
                          </button>
                          {String(row.rowStatus || '').toUpperCase() !== 'PROMOTED' && (
                            <button
                              title="Edit row"
                              style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#2563eb', cursor: 'pointer' }}
                              onClick={() => handleEditRow(row)}
                            >
                              <MdOutlineEdit size={15} />
                            </button>
                          )}
                          {String(row.rowStatus || '').toUpperCase() !== 'PROMOTED' && (
                            <button
                              title={isPromotingThisRow ? 'Promoting...' : 'Promote row'}
                              style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #bfdbfe', borderRadius: '6px', background: 'white', color: '#2563eb', cursor: (isPromotingThisRow || isProcessing) ? 'not-allowed' : 'pointer' }}
                              onClick={() => openPromoteModal(row)}
                              disabled={isPromotingThisRow || isProcessing || promoteModalRow}
                            >
                              <ArrowUpCircle size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {viewingRow && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
          onClick={() => setViewingRow(null)}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '960px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Batch Details</h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Row #{formatRowNumber(viewingRow.sheetRowNumber)} &bull; {viewingRow.batchNumber || '-'}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className="job-status-badge job-status-badge--emphasis" style={getRowStatusStyle(viewingRow.rowStatus)}>
                  {viewingRow.rowStatus || 'UNKNOWN'}
                </span>
                <button onClick={() => setViewingRow(null)}
                  style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
            </div>

            {(viewingRow.validationErrors?.length > 0 || viewingRow.validationErrorInfo) && (
              <div style={{ marginBottom: '20px' }}>
                <ErrorsBlock validationErrors={viewingRow.validationErrors} validationErrorInfo={viewingRow.validationErrorInfo} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 24px' }}>
              <DetailRow label="Check Number" value={viewingRow.checkNumber} />
              <DetailRow label="Practice Code" value={viewingRow.practiceCode} />
              <DetailRow label="Location Code" value={viewingRow.locationCode} />
              <DetailRow label="Check Type" value={formatCheckTypeForDisplay(viewingRow.checkType, checkTypes)} />
              <DetailRow label="Deposit Date" value={viewingRow.depositDate ? formatDateUS(viewingRow.depositDate) : null} />
              <DetailRow label="Check Amount" value={viewingRow.checkAmount != null ? formatCurrency(viewingRow.checkAmount) : null} />
              <DetailRow label="Batch Type" value={viewingRow.batchType} />
              <DetailRow label="Batch Number" value={viewingRow.batchNumber} />
              <DetailRow label="Run Number" value={viewingRow.batchRunNumber} />
              <DetailRow label="Invoice Number" value={viewingRow.invoiceNumber} />
              <DetailRow label="EDM Number" value={viewingRow.edmNumber} />
              <DetailRow label="Batch Date" value={viewingRow.batchDate ? formatDateUS(viewingRow.batchDate) : null} />
              <DetailRow label="Batch Amount" value={viewingRow.batchAmount != null ? formatCurrency(viewingRow.batchAmount) : null} />
              <DetailRow label="Correction Batch" value={String(viewingRow.correctionBatch ?? '')} />
              <DetailRow label="Batch Source" value={viewingRow.batchSource} />
              <DetailRow label="Last Validated" value={formatDateTime(viewingRow.lastValidatedAt)} />
              <DetailRow label="Created At" value={formatDateTime(viewingRow.createdAt)} />
              <DetailRow label="Updated At" value={formatDateTime(viewingRow.updatedAt)} />
              <DetailRow label="Batch Notes" value={viewingRow.batchNotes} wide />
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => setViewingRow(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editingRow && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '980px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Edit Batch</h3>
              <button onClick={() => { setEditingRow(null); setEditFormData(EMPTY_EDIT); setRowFormPracticeId(''); }} disabled={savingEdit}
                style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#6b7280' }}>Row #{formatRowNumber(editingRow.sheetRowNumber)} &bull; {editingRow.batchNumber || '-'} — Saves and auto re-validates</p>

            {(editingRow.validationErrors?.length > 0 || editingRow.validationErrorInfo) && (
              <div style={{ marginBottom: '16px' }}>
                <ErrorsBlock validationErrors={editingRow.validationErrors} validationErrorInfo={editingRow.validationErrorInfo} />
              </div>
            )}

            <div className="batch-modal-content" style={{ marginTop: '8px' }}>
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Check Number</label>
                  <input type="text" value={editFormData.checkNumber} onChange={(e) => setEditFormData((p) => ({ ...p, checkNumber: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Practice</label>
                  <SearchableDropdown
                    options={loadingPractices ? [{ value: '', label: 'Loading...' }] : practiceOptions}
                    value={rowFormPracticeId}
                    onChange={(val) => {
                      setRowFormPracticeId(val);
                      const found = practices.find((p) => (p.practiceId || p.id) === val);
                      setEditFormData((p) => ({ ...p, practiceCode: found?.code || '', locationCode: '' }));
                    }}
                    placeholder="Select Practice"
                    compact={true}
                    disabled={loadingPractices}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Location</label>
                  <SearchableDropdown
                    options={loadingRowFormLocations ? [{ value: '', label: 'Loading...' }] : rowFormLocationOptions}
                    value={editFormData.locationCode}
                    onChange={(val) => setEditFormData((p) => ({ ...p, locationCode: val }))}
                    placeholder={!rowFormPracticeId ? 'Select practice first' : 'Select Location'}
                    compact={true}
                    disabled={!rowFormPracticeId || loadingRowFormLocations}
                  />
                </div>
              </div>
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Check Type</label>
                  <SearchableDropdown
                    options={[{ value: '', label: 'Select Check Type' }, ...(checkTypes || []).map((ct) => ({ value: ct.code, label: ct.name }))]}
                    value={editFormData.checkType}
                    onChange={(val) => setEditFormData((p) => ({ ...p, checkType: val }))}
                    placeholder="Select Check Type"
                    compact={true}
                    disabled={checkTypesLoading}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Deposit Date</label>
                  <USDateInput name="depositDate" value={editFormData.depositDate} onChange={(e) => setEditFormData((p) => ({ ...p, depositDate: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Check Amount</label>
                  <input type="number" step="0.01" value={editFormData.checkAmount} onChange={(e) => setEditFormData((p) => ({ ...p, checkAmount: e.target.value }))} />
                </div>
              </div>

              <div style={{ marginTop: '8px', marginBottom: '4px', fontSize: '13px', fontWeight: '700', color: '#111827' }}>Batch</div>

              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Type</label>
                  <select value={editFormData.batchType} onChange={(e) => handleEditBatchFormChange('batchType', e.target.value)}>
                    <option value="MANUAL">MANUAL</option>
                    <option value="AUTO">AUTO</option>
                  </select>
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Source <span style={{ color: '#dc2626' }}>*</span></label>
                  <select value={editFormData.batchSource || 'Athena'} onChange={(e) => handleEditBatchFormChange('batchSource', e.target.value)}>
                    <option value="Athena">Athena</option>
                    <option value="Centricity">Centricity</option>
                  </select>
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Date</label>
                  <USDateInput name="batchDate" value={editFormData.batchDate} onChange={(e) => setEditFormData((p) => ({ ...p, batchDate: e.target.value }))} placeholder="MM/DD/YYYY" />
                </div>
              </div>

              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Run Number</label>
                  <input
                    type="text"
                    value={editFormData.batchRunNumber}
                    onChange={(e) => handleEditBatchFormChange('batchRunNumber', e.target.value)}
                    disabled={isRunNumberDisabledForBatchType(editFormData.batchType)}
                    readOnly={isRunNumberDisabledForBatchType(editFormData.batchType)}
                    placeholder={isRunNumberDisabledForBatchType(editFormData.batchType) ? 'N/A (MANUAL batch)' : 'Enter run number'}
                    style={{
                      backgroundColor: isRunNumberDisabledForBatchType(editFormData.batchType) ? '#f3f4f6' : 'white',
                      cursor: isRunNumberDisabledForBatchType(editFormData.batchType) ? 'not-allowed' : 'text',
                    }}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Number</label>
                  <input type="text" value={editFormData.batchNumber} onChange={(e) => setEditFormData((p) => ({ ...p, batchNumber: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">EDM Number</label>
                  <input
                    type="text"
                    value={editFormData.edmNumber}
                    onChange={(e) => handleEditBatchFormChange('edmNumber', e.target.value)}
                    disabled={isEdmNumberDisabledForBatchType(editFormData.batchType)}
                    readOnly={isEdmNumberDisabledForBatchType(editFormData.batchType)}
                    placeholder={isEdmNumberDisabledForBatchType(editFormData.batchType) ? 'N/A (AUTO batch)' : 'Enter EDM number'}
                    style={{
                      backgroundColor: isEdmNumberDisabledForBatchType(editFormData.batchType) ? '#f3f4f6' : 'white',
                      cursor: isEdmNumberDisabledForBatchType(editFormData.batchType) ? 'not-allowed' : 'text',
                    }}
                  />
                </div>
              </div>

              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Invoice Number</label>
                  <input type="text" value={editFormData.invoiceNumber} onChange={(e) => setEditFormData((p) => ({ ...p, invoiceNumber: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Amount</label>
                  <input type="number" step="0.01" value={editFormData.batchAmount} onChange={(e) => setEditFormData((p) => ({ ...p, batchAmount: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Correction Batch</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', paddingTop: '2px' }}>
                    <input
                      type="checkbox"
                      checked={editFormData.correctionBatch || false}
                      onChange={(e) => handleEditBatchFormChange('correctionBatch', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>{editFormData.correctionBatch ? 'Yes' : 'No'}</span>
                  </label>
                </div>
              </div>

              <div className="batch-modal-field batch-modal-field-full">
                <label className="batch-modal-label">Notes</label>
                <textarea value={editFormData.batchNotes} onChange={(e) => setEditFormData((p) => ({ ...p, batchNotes: e.target.value }))} rows={3} />
              </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-cancel" onClick={() => { setEditingRow(null); setEditFormData(EMPTY_EDIT); setRowFormPracticeId(''); }} disabled={savingEdit}>Cancel</button>
              <button className="btn-upload" onClick={handleSaveEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save & Re-validate'}</button>
            </div>
          </div>
        </div>
      )}

      {promoteModalRow && promoteFormData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
          onClick={closePromoteModal}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '980px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Promote row</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                  Row #{formatRowNumber(promoteModalRow.sheetRowNumber)} — Edit staged values, then promote to create the batch
                </p>
              </div>
              <button type="button" onClick={closePromoteModal} disabled={promotingFromModal}
                style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6b7280', cursor: promotingFromModal ? 'not-allowed' : 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {(promoteModalRow.validationErrors?.length > 0 || promoteModalRow.validationErrorInfo) && (
              <div style={{ marginBottom: '16px' }}>
                <ErrorsBlock validationErrors={promoteModalRow.validationErrors} validationErrorInfo={promoteModalRow.validationErrorInfo} />
              </div>
            )}

            <div className="batch-modal-content" style={{ marginTop: '8px' }}>
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Check Number</label>
                  <input type="text" value={promoteFormData.checkNumber} onChange={(e) => setPromoteFormData((p) => ({ ...p, checkNumber: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Practice</label>
                  <SearchableDropdown
                    options={loadingPractices ? [{ value: '', label: 'Loading...' }] : practiceOptions}
                    value={rowFormPracticeId}
                    onChange={(val) => {
                      setRowFormPracticeId(val);
                      const found = practices.find((p) => (p.practiceId || p.id) === val);
                      setPromoteFormData((p) => (p ? { ...p, practiceCode: found?.code || '', locationCode: '' } : p));
                    }}
                    placeholder="Select Practice"
                    compact={true}
                    disabled={loadingPractices || promotingFromModal}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Location</label>
                  <SearchableDropdown
                    options={loadingRowFormLocations ? [{ value: '', label: 'Loading...' }] : rowFormLocationOptions}
                    value={promoteFormData.locationCode}
                    onChange={(val) => setPromoteFormData((p) => (p ? { ...p, locationCode: val } : p))}
                    placeholder={!rowFormPracticeId ? 'Select practice first' : 'Select Location'}
                    compact={true}
                    disabled={!rowFormPracticeId || loadingRowFormLocations || promotingFromModal}
                  />
                </div>
              </div>
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Check Type</label>
                  <SearchableDropdown
                    options={[{ value: '', label: 'Select Check Type' }, ...(checkTypes || []).map((ct) => ({ value: ct.code, label: ct.name }))]}
                    value={promoteFormData.checkType}
                    onChange={(val) => setPromoteFormData((p) => ({ ...p, checkType: val }))}
                    placeholder="Select Check Type"
                    compact={true}
                    disabled={checkTypesLoading}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Deposit Date</label>
                  <USDateInput name="promoteDepositDate" value={promoteFormData.depositDate} onChange={(e) => setPromoteFormData((p) => ({ ...p, depositDate: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Check Amount</label>
                  <input type="number" step="0.01" value={promoteFormData.checkAmount} onChange={(e) => setPromoteFormData((p) => ({ ...p, checkAmount: e.target.value }))} />
                </div>
              </div>

              <div style={{ marginTop: '8px', marginBottom: '4px', fontSize: '13px', fontWeight: '700', color: '#111827' }}>Batch</div>

              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Type</label>
                  <select value={promoteFormData.batchType} onChange={(e) => handlePromoteBatchFormChange('batchType', e.target.value)}>
                    <option value="MANUAL">MANUAL</option>
                    <option value="AUTO">AUTO</option>
                  </select>
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Source <span style={{ color: '#dc2626' }}>*</span></label>
                  <select value={promoteFormData.batchSource || 'Athena'} onChange={(e) => handlePromoteBatchFormChange('batchSource', e.target.value)}>
                    <option value="Athena">Athena</option>
                    <option value="Centricity">Centricity</option>
                  </select>
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Date</label>
                  <USDateInput name="promoteBatchDate" value={promoteFormData.batchDate} onChange={(e) => setPromoteFormData((p) => ({ ...p, batchDate: e.target.value }))} placeholder="MM/DD/YYYY" />
                </div>
              </div>

              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Run Number</label>
                  <input
                    type="text"
                    value={promoteFormData.batchRunNumber}
                    onChange={(e) => handlePromoteBatchFormChange('batchRunNumber', e.target.value)}
                    disabled={isRunNumberDisabledForBatchType(promoteFormData.batchType)}
                    readOnly={isRunNumberDisabledForBatchType(promoteFormData.batchType)}
                    placeholder={isRunNumberDisabledForBatchType(promoteFormData.batchType) ? 'N/A (MANUAL batch)' : 'Enter run number'}
                    style={{
                      backgroundColor: isRunNumberDisabledForBatchType(promoteFormData.batchType) ? '#f3f4f6' : 'white',
                      cursor: isRunNumberDisabledForBatchType(promoteFormData.batchType) ? 'not-allowed' : 'text',
                    }}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Number</label>
                  <input type="text" value={promoteFormData.batchNumber} onChange={(e) => setPromoteFormData((p) => ({ ...p, batchNumber: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">EDM Number</label>
                  <input
                    type="text"
                    value={promoteFormData.edmNumber}
                    onChange={(e) => handlePromoteBatchFormChange('edmNumber', e.target.value)}
                    disabled={isEdmNumberDisabledForBatchType(promoteFormData.batchType)}
                    readOnly={isEdmNumberDisabledForBatchType(promoteFormData.batchType)}
                    placeholder={isEdmNumberDisabledForBatchType(promoteFormData.batchType) ? 'N/A (AUTO batch)' : 'Enter EDM number'}
                    style={{
                      backgroundColor: isEdmNumberDisabledForBatchType(promoteFormData.batchType) ? '#f3f4f6' : 'white',
                      cursor: isEdmNumberDisabledForBatchType(promoteFormData.batchType) ? 'not-allowed' : 'text',
                    }}
                  />
                </div>
              </div>

              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Invoice Number</label>
                  <input type="text" value={promoteFormData.invoiceNumber} onChange={(e) => setPromoteFormData((p) => ({ ...p, invoiceNumber: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Amount</label>
                  <input type="number" step="0.01" value={promoteFormData.batchAmount} onChange={(e) => setPromoteFormData((p) => ({ ...p, batchAmount: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Correction Batch</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', paddingTop: '2px' }}>
                    <input
                      type="checkbox"
                      checked={promoteFormData.correctionBatch || false}
                      onChange={(e) => handlePromoteBatchFormChange('correctionBatch', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>{promoteFormData.correctionBatch ? 'Yes' : 'No'}</span>
                  </label>
                </div>
              </div>

              <div className="batch-modal-field batch-modal-field-full">
                <label className="batch-modal-label">Notes</label>
                <textarea value={promoteFormData.batchNotes} onChange={(e) => setPromoteFormData((p) => ({ ...p, batchNotes: e.target.value }))} rows={3} />
              </div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
              <button type="button" className="btn-cancel" onClick={closePromoteModal} disabled={promotingFromModal}>Cancel</button>
              <button type="button" className="btn-upload" onClick={handleConfirmPromoteFromModal} disabled={promotingFromModal}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowUpCircle size={18} />
                {promotingFromModal ? 'Promoting...' : 'Promote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="upload-modal-content" style={{ maxWidth: '420px' }}>
            <h3 style={{ marginTop: 0, color: '#dc2626' }}>Confirm Delete Job</h3>
            <p>Delete job "<strong>{jobToDelete?.jobName || 'Unnamed Job'}</strong>"? This removes all staged rows permanently.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-cancel" onClick={() => { setShowDeleteConfirm(false); setJobToDelete(null); }}>Cancel</button>
              <button style={{ padding: '8px 20px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white' }} onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchUpload;
